import { sectionRenderer } from '@theme/section-renderer';

/**
 * Wires "Încarcă mai multe" to the Section Rendering API (SPEC §5.2.2, §5.3,
 * §6.8).
 *
 * `snippets/pagination-progress.liquid` renders a real link to the next page
 * and `assets/pagination-progress.js` upgrades it into a request for someone
 * to load that page in place — deliberately without knowing how. This is the
 * "how" for the collection and search grids: fetch the next page of *this*
 * section, take its cards, append them, and update the two numbers and the bar
 * that describe the result.
 *
 * ## Why not `paginated-list.js`
 *
 * That component pages by observing sentinels — it is infinite scroll, and its
 * loader is private. The design asks for an explicit control with a progress
 * bar, which is a different interaction with a different accessibility
 * contract: focus moves to the first new card, and the reader is told how far
 * through the list they now are. Both live in the theme and the section picks
 * one; they are never both active, because the sentinels are only rendered
 * when infinite scroll is on.
 *
 * ## The four things that go wrong here, and what stops each
 *
 * 1. **Duplicated cards after a filter change** (QA-24). The response is asked
 *    for the cards it holds, and any whose `data-product-id` is already in the
 *    grid is dropped. A filter that changed mid-load cannot double a product.
 * 2. **Racing requests.** One in-flight load at a time; a second click while
 *    one is running is ignored rather than queued behind it.
 * 3. **A dead button.** Every exit path calls `complete()`, including the
 *    failures — without it the control stays `aria-busy` forever and the only
 *    fix is a reload.
 * 4. **A stale progress line.** The bar, the sentence and the button's own
 *    count are not recomputed here — the page that was just fetched already
 *    contains all three, rendered by the same Liquid, and Romanian pluralises
 *    in three forms that this module is not going to try to reproduce. So the
 *    whole control is swapped for the incoming one.
 */

/** @type {boolean} */
let loading = false;

document.addEventListener('pagination-progress:load', handleLoad);

/**
 * @param {Event} event
 */
async function handleLoad(event) {
  if (!(event instanceof CustomEvent)) return;

  const control = event.target;
  if (!(control instanceof HTMLElement)) return;

  const root = control.closest('results-list');
  const grid = root?.querySelector('[ref="grid"]');
  if (!(root instanceof HTMLElement) || !(grid instanceof HTMLElement)) return;

  const sectionId = root.getAttribute('section-id');
  if (!sectionId) return;

  // Claiming the event is what tells the control not to navigate. Claim it
  // before any await, or a slow network turns a click into a page load.
  event.preventDefault();

  const { url, complete } = event.detail;

  if (loading) {
    complete();
    return;
  }
  loading = true;

  try {
    const html = await sectionRenderer.getSectionHTML(sectionId, false, new URL(url, window.location.origin));
    const parsed = new DOMParser().parseFromString(html, 'text/html');

    const incoming = parsed.querySelectorAll('[ref="grid"] > [ref="cards[]"]');
    const seen = new Set(
      [...grid.querySelectorAll(':scope > [ref="cards[]"]')].map((card) =>
        card instanceof HTMLElement ? card.dataset.productId : null
      )
    );

    const fragment = document.createDocumentFragment();
    let appended = 0;

    for (const card of incoming) {
      if (!(card instanceof HTMLElement)) continue;
      if (card.dataset.productId && seen.has(card.dataset.productId)) continue;

      fragment.appendChild(card);
      appended += 1;
    }

    // Nothing new means the list is exhausted — or the filters changed under
    // the request. Either way the control has no next page to offer.
    if (appended === 0) {
      removeControl(control);
      complete();
      return;
    }

    // The first appended card, captured before the fragment empties itself.
    const firstNew = fragment.firstElementChild;
    grid.appendChild(fragment);

    swapProgress(root, parsed);

    complete(firstNew ?? grid);
  } catch {
    // A failed load leaves the page exactly as it was, with a working link.
    complete();
  } finally {
    loading = false;
  }
}

/**
 * Replaces the progress control with the one the fetched page rendered.
 *
 * That control describes the list *after* this page landed — same button
 * count, same bar, same sentence — because it was rendered by the same Liquid
 * against the same pagination. Recomputing any of it here would be a second
 * implementation of §6.8 that could disagree with the first.
 *
 * @param {HTMLElement} root
 * @param {Document} parsed
 */
function swapProgress(root, parsed) {
  const current = root.querySelector('.pagination-progress');
  if (!(current instanceof HTMLElement)) return;

  const incoming = parsed.querySelector('.pagination-progress');

  if (incoming) {
    current.replaceWith(incoming);
  } else {
    // The last page renders no control at all.
    current.remove();
  }
}

/**
 * @param {HTMLElement} control
 */
function removeControl(control) {
  const wrapper = control.closest('.pagination-progress');
  (wrapper ?? control).remove();
}
